import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { onAuthStateChanged, signOut } from "firebase/auth";
import { doc, getDoc } from "firebase/firestore";
import { auth, db } from "../firebase";
import {
    effectiveUserDataForVenue,
    isMultiVenueEnabled,
    resolveVenueAccess,
    venueSessionKey,
} from "../lib/venueAccess";

const AuthContext = createContext();

export const AuthProvider = ({ children }) => {
    const [currentUser, setCurrentUser] = useState(null);
    const [rawUserData, setRawUserData] = useState(null);
    const [venueOptions, setVenueOptions] = useState([]);
    const [activeVenue, setActiveVenue] = useState(null);
    const [authError, setAuthError] = useState(null);
    const [loading, setLoading] = useState(true);
    const multiVenueEnabled = isMultiVenueEnabled();

    useEffect(() => {
        let cancelled = false;
        let authRequestId = 0;

        const unsubscribe = onAuthStateChanged(auth, async (user) => {
            const requestId = ++authRequestId;
            if (cancelled) return;
            setCurrentUser(user);
            setLoading(true);
            setAuthError(null);

            if (user) {
                try {
                    const userSnapshot = await getDoc(doc(db, "users", user.uid));
                    if (cancelled || requestId !== authRequestId) return;

                    const profile = userSnapshot.exists() ? userSnapshot.data() : null;
                    const access = resolveVenueAccess(profile, multiVenueEnabled);
                    const labeledAccess = await Promise.all(access.map(async (entry) => {
                        try {
                            const businessSnapshot = await getDoc(doc(db, "businesses", entry.businessID));
                            const business = businessSnapshot.exists() ? businessSnapshot.data() : null;
                            return {
                                ...entry,
                                businessName: business?.displayName
                                    || business?.name
                                    || business?.legalName
                                    || entry.businessID,
                            };
                        } catch (error) {
                            console.warn(`Unable to load venue label for ${entry.businessID}:`, error);
                            return {...entry, businessName: entry.businessID};
                        }
                    }));

                    if (cancelled || requestId !== authRequestId) return;

                    let selectedVenue = null;
                    if (profile?.role !== "ADMIN") {
                        if (labeledAccess.length === 1) {
                            selectedVenue = labeledAccess[0];
                        } else if (labeledAccess.length > 1) {
                            const storedBusinessID = sessionStorage.getItem(venueSessionKey(user.uid));
                            selectedVenue = labeledAccess.find(
                                (entry) => entry.businessID === storedBusinessID,
                            ) || null;
                            if (!selectedVenue && storedBusinessID) {
                                sessionStorage.removeItem(venueSessionKey(user.uid));
                            }
                        }
                    }

                    setRawUserData(profile);
                    setVenueOptions(labeledAccess);
                    setActiveVenue(selectedVenue);
                } catch (error) {
                    if (cancelled || requestId !== authRequestId) return;
                    console.error("Unable to load authenticated user data:", error);
                    setRawUserData(null);
                    setVenueOptions([]);
                    setActiveVenue(null);
                    setAuthError("Unable to load your account. Please try again.");
                }
            } else {
                setRawUserData(null);
                setVenueOptions([]);
                setActiveVenue(null);
            }

            if (!cancelled && requestId === authRequestId) setLoading(false);
        });

        return () => {
            cancelled = true;
            unsubscribe();
        };
    }, [multiVenueEnabled]);

    const selectVenue = useCallback((businessID) => {
        const selectedVenue = venueOptions.find((entry) => entry.businessID === businessID);
        if (!selectedVenue || !currentUser) return null;

        sessionStorage.setItem(venueSessionKey(currentUser.uid), selectedVenue.businessID);
        setActiveVenue(selectedVenue);
        return selectedVenue;
    }, [currentUser, venueOptions]);

    const clearVenueSelection = useCallback(() => {
        if (currentUser) sessionStorage.removeItem(venueSessionKey(currentUser.uid));
        setActiveVenue(null);
    }, [currentUser]);

    const logout = async () => {
        try {
            if (currentUser) sessionStorage.removeItem(venueSessionKey(currentUser.uid));
            await signOut(auth);
        } catch (error) {
            console.error("Error logging out:", error);
        }
    };

    const userData = useMemo(() => {
        if (rawUserData?.role === "ADMIN") return rawUserData;
        return activeVenue ? effectiveUserDataForVenue(rawUserData, activeVenue) : null;
    }, [activeVenue, rawUserData]);
    const venueSelectionRequired = !!currentUser
        && !loading
        && rawUserData?.role !== "ADMIN"
        && venueOptions.length !== 1
        && !activeVenue;

    return (
        <AuthContext.Provider value={{
            currentUser,
            rawUserData,
            userData,
            venueOptions,
            activeVenue,
            venueSelectionRequired,
            multiVenueEnabled,
            authError,
            loading,
            selectVenue,
            clearVenueSelection,
            logout,
        }}>
            {children}
        </AuthContext.Provider>
    );
};

export const useAuth = () => useContext(AuthContext);
